import { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { AuthService } from '../services/AuthService';

/**
 * A local implementation of the useAuthState hook that uses our Backend API.
 * Pass refreshTrigger and increment it after login/register to re-fetch current user.
 */
export function useAuthState(
  refreshTrigger?: number,
): [User | null | undefined, boolean, Error | undefined, boolean] {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [isSuperuser, setIsSuperuser] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      try {
        const currentUser = await AuthService.getCurrentUser();
        if (isMounted) {
          setUser(currentUser);
          setIsSuperuser(Boolean(currentUser) && AuthService.isSuperuser());
          setLoading(false);
        }
      } catch (err: any) {
        if (isMounted) {
          setUser(null);
          setIsSuperuser(false);
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      }
    };

    checkAuth();

    return () => {
      isMounted = false;
    };
  }, [refreshTrigger ?? 0]);

  return [user, loading, error, isSuperuser];
}
